import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import Button from "../Button";
import { showToast } from "../../utils/toast";
import { useUploadPassenger } from "../../services/queries/passengers";
import { parseError } from "../../utils";

const schema = yup.object().shape({
  file: yup
    .mixed<File>()
    .required("You need to upload a CSV file")
    .test("fileType", "Only CSV files are allowed", (file) => {
      if (!file) return false;
      // Some browsers may not set type consistently; fallback to name check
      return file.type === "text/csv" || /\.csv$/i.test(file.name);
    })
    .test("fileSize", "Max size is 4MB", (file) => {
      if (!file) return false;
      return file.size <= 4 * 1024 * 1024;
    }),
});

interface FormValues {
  file: File;
}

interface UploadPassengerFormProps {
  businessId: string;
  onClose: () => void;
}

export default function UploadPassengerForm({
  businessId,
  onClose,
}: UploadPassengerFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const { mutate: uploadPassengers, isLoading } = useUploadPassenger();

  const handleFormSubmit = async (formData: FormValues) => {
    if (!businessId || !formData.file) return;

    uploadPassengers(
      {
        businessId,
        data: formData,
      },
      {
        onSuccess: () => {
          showToast("Passengers uploaded successfully", "success");
          onClose();
          reset();
        },
        onError: (error: unknown) => {
          const message = parseError(error);

          showToast(message, "error");
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="form-group mb-5">
        <label className="block text-gray-700 text-sm font-medium mb-1">
          Upload File
        </label>

        <Controller
          name="file"
          control={control}
          render={({ field: { onChange } }) => (
            <input
              title="Upload CSV File"
              type="file"
              accept=".csv"
              onChange={(e) => {
                const selectedFile = e.target.files?.[0] || null;
                onChange(selectedFile);
              }}
            />
          )}
        />

        {errors.file && (
          <p className="text-red-500 text-xs">{errors.file.message}</p>
        )}
      </div>

      <Button
        type="submit"
        disabled={isLoading}
        loading={isLoading}
        variant="primary"
      >
        {isLoading ? "Uploading..." : "Upload"}
      </Button>
    </form>
  );
}
